import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@mdi/react';
import { mdiMapMarker, mdiFileSign, mdiAirFilter, mdiClipboardList, mdiDomain, mdiFileCompare, mdiCircle, mdiShareVariant } from '@mdi/js';
import QuoteSetupModal from './QuoteSetupModal';
import FeaturesModal from './FeaturesModal';
import AnnuitiesApi from '../../utils/AnnuitiesApi';
import { AMBEST_SORTING } from 'constants/App';

const QuoterHeader = ({
  onTabChange,
  activeTab,
  isUniversalQuoterLoaded = true,
  compareSelections = {},
  onCompareAction,
  quotesData = null,
  originalQuotesData = null,
  onFiltersApply,
  onFiltersClear,
  hasActiveFilters = false,
  onModalOpen,
  onQuoteSetupToggle,
  currentView = 'form', // 'form', 'loading', or 'results'
  formData,
  onShareAction,
  universalQuoterDetails
}) => {
  const [selectedActionItem, setSelectedActionItem] = React.useState(null);
  const [isQuoteSetupModalOpen, setIsQuoteSetupModalOpen] = React.useState(false);
  const [isFeaturesModalOpen, setIsFeaturesModalOpen] = React.useState(false);
  const [isAlqFiltersModalOpen, setIsAlqFiltersModalOpen] = React.useState(false);
  const [hoveredTab, setHoveredTab] = React.useState(null);
  const headerRef = React.useRef(null);
  const isApplyingFilters = React.useRef(false);

  // State for market indices
  const [availableIndexes, setAvailableIndexes] = React.useState([]);
  const [indexesLoading, setIndexesLoading] = React.useState(false);

  // Filter states for ALQ
  const [alqFilters, setAlqFilters] = React.useState({
    healthClass: [],
    flexibleCoverage: [],
    acceleratedDeathBenefit: [],
    additionalRiders: [],
    iulRiders: [],
    easyIssue: [],
    promotions: [],
    eSubmission: [],
    amBestRating: ''
  });

  // Filter states for AAQ
  const [aaqFilters, setAaqFilters] = React.useState({
    riders: {
      livingBenefitRider: false,
      ageRate: {
        age: '',
        rate: ''
      }
    },
    surrenderCharge: [],
    guaranteePeriod: [],
    mva: false,
    rates: {
      minimumGuaranteePercent: '',
      minimumCurrentRate: ''
    },
    premiumType: [],
    availableIndexes: [],
    amBestRating: ''
  });

  const [availableAmBestRatings, setAvailableAmBestRatings] = React.useState([]);

  // Clear selected action item when tab changes
  React.useEffect(() => {
    setSelectedActionItem(null);
  }, [activeTab]);

  // Reset filters only when a new quote request is initiated (currentView === 'loading')
  const prevViewRef = React.useRef(currentView);
  React.useEffect(() => {
    // Only reset filters when transitioning TO loading state (new quote request)
    if (currentView === 'loading' && prevViewRef.current !== 'loading') {
      // Reset ALQ filters
      setAlqFilters({
        healthClass: [],
        flexibleCoverage: [],
        acceleratedDeathBenefit: [],
        additionalRiders: [],
        iulRiders: [],
        easyIssue: [],
        promotions: [],
        eSubmission: [],
        amBestRating: ''
      });

      // Reset AAQ filters
      setAaqFilters({
        riders: {
          livingBenefitRider: false,
          ageRate: {
            age: '',
            rate: ''
          }
        },
        surrenderCharge: [],
        guaranteePeriod: [],
        mva: false,
        rates: {
          minimumGuaranteePercent: '',
          minimumCurrentRate: ''
        },
        premiumType: [],
        amBestRating: '',
        availableIndexes: []
      });
    }
    prevViewRef.current = currentView;
    isApplyingFilters.current = false;
  }, [currentView]);

  React.useEffect(() => {
    const handleClickOutside = event => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setSelectedActionItem(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load market indices when modal opens and we have FIA products
  React.useEffect(() => {
    if (isAlqFiltersModalOpen && activeTab === 'new-annuity' && hasFiaProducts() && availableIndexes.length === 0 && !indexesLoading) {
      setIndexesLoading(true);
      AnnuitiesApi.loadMarketIndicies()
        .then(response => {
          if (response && response.data) {
            const indexOptions = response.data.map(index => ({
              value: index.id || index.value,
              label: index.name || index.label
            }));
            setAvailableIndexes(indexOptions);
          }
        })
        .catch(error => {
          console.error('Failed to load market indices:', error);
        })
        .finally(() => {
          setIndexesLoading(false);
        });
    }
  }, [isAlqFiltersModalOpen, activeTab, availableIndexes.length, indexesLoading]);

  // Extract available AM Best ratings from quotes
  React.useEffect(() => {
    if ((activeTab === 'new-annuity' || activeTab === 'new-life') && originalQuotesData && Array.isArray(originalQuotesData)) {
      // Get unique AM Best ratings from quotes
      const ratingsSet = new Set();
      let hasNullRatings = false;

      originalQuotesData.forEach(quote => {
        const rating = quote.am_best_rating;

        // Check if rating is null, undefined, or empty string
        if (rating === null || rating === undefined || rating === '') {
          hasNullRatings = true;
        } else if (typeof rating === 'string' && rating.trim() !== '') {
          // Only add valid string ratings
          ratingsSet.add(rating);
        }
      });

      // Convert to array and sort by AMBEST_SORTING order (highest to lowest)
      const sortedRatings = Array.from(ratingsSet).sort((a, b) => {
        return (AMBEST_SORTING[b] || 0) - (AMBEST_SORTING[a] || 0);
      });

      // Add "NA" at the end if there are quotes with null ratings
      if (hasNullRatings) {
        sortedRatings.push('NA');
      }

      setAvailableAmBestRatings(sortedRatings);
    } else if (activeTab === 'new-annuity' && (!originalQuotesData || originalQuotesData.length === 0)) {
      // Clear ratings if no quotes
      setAvailableAmBestRatings([]);
    }
  }, [activeTab, originalQuotesData]);

  // Helper function to check if FIA products exist in quotes
  const hasFiaProducts = () => {
    if (!quotesData && !originalQuotesData) return false;

    const dataToCheck = quotesData || originalQuotesData || [];
    return dataToCheck.some(quote => {
      const productType = quote.product_type || '';
      const productName = (quote.product_name || '').toLowerCase();
      return productType === 'FIA' || productType === 'Fixed Indexed Annuity' || productName.includes('fixed indexed') || productName.includes('indexed annuity');
    });
  };

  const hasIULProducts = () => {
    if (activeTab === 'new-life' && formData?.selectedProductTypes) {
      return formData.selectedProductTypes.some(productType => {
        const productTypeStr = String(productType || '').toUpperCase();
        return productTypeStr === 'IUL' || productTypeStr.includes('( IUL )') || productTypeStr.includes('(IUL)') || productTypeStr.includes('INDEXED UNIVERSAL');
      });
    }
  };
  // Filter configuration for ALQ
  const alqFilterConfig = {
    ...(formData?.healthClass?.length > 1 && {
      healthClass: {
        label: 'Health Classes',
        options: formData?.healthClass
      }
    }),
    flexibleCoverage: {
      label: 'Flexible Coverage',
      options: ['Increase Face Amount', 'Decrease Face Amount', 'Convertability', 'Renewable']
    },
    acceleratedDeathBenefit: {
      label: 'Accelerated Death Benefit Riders',
      options: ['Critical Illness', 'Chronic Illness', 'Terminal Illness']
    },
    additionalRiders: {
      label: 'Additional Riders',
      options: [
        'Common Carrier Accidental Death Benefit',
        'Cost of Living Increase',
        'Family Health Benefit',
        'Nursing Home Confinement',
        'Residential Damage Benefit',
        'Waiver of Premium for Unemployment'
      ]
    },
    ...(hasIULProducts() && {
      iulRiders: {
        label: 'IUL Riders',
        options: [
          'Overloan Protection Rider',
          'LTC Rider',
          'Disability Income Rider',
          'Enhanced Surrender Value Rider',
          'Cost of Living Rider',
          'Spouse Term',
          'Guaranteed Insurability',
          'Income Benefits',
          'No Lapse Rider',
          'Automatic Premium Loan Rider',
          'Index Participation Rider'
        ]
      }
    }),
    easyIssue: {
      label: 'Easy Issue',
      options: ['Accelerated Underwriting', 'Instant Issue', 'Non Medical (Simplified Issue)']
    },
    promotions: {
      label: 'Promotions',
      options: ['No-Cost Wearable', 'Discount Program']
    },
    eSubmission: {
      label: 'eSubmission',
      options: ['Firelight eSubmission', 'Simplified eTicket']
    },
    amBestRating: {
      label: 'AM Best Rating',
      type: 'dropdown',
      options: availableAmBestRatings,
      placeholder: 'Select minimum rating'
    }
  };

  // Filter configuration for AAQ
  const aaqFilterConfig = {
    riders: {
      label: 'Riders',
      type: 'riders',
      options: [
        {
          key: 'livingBenefitRider',
          label: 'Living Benefit Rider (GMWB)',
          type: 'checkbox'
        },
        {
          key: 'ageRate',
          label: 'Age-Based Rate',
          type: 'age-rate',
          ageOptions: [50, 55, 60, 65, 70]
        }
      ]
    },
    surrenderCharge: {
      label: 'Surrender Charge',
      options: ['0 years', '1 year', '2 years', '3 years', '4 years', '5 years', '6 years', '7 years', '8 years', '9 years', '10+ years']
    },
    guaranteePeriod: {
      label: 'Guarantee Period',
      options: ['0 years', '1 year', '2 years', '3 years', '4 years', '5 years', '6 years', '7 years', '8 years', '9 years', '10+ years']
    },
    mva: {
      label: 'MVA',
      options: ['Include MVA Products'],
      type: 'checkbox'
    },
    rates: {
      label: 'Rates',
      type: 'rates',
      options: [
        {
          key: 'minimumGuaranteePercent',
          label: 'Minimum Guarantee Percent',
          type: 'percentage',
          placeholder: 'Enter minimum %'
        },
        {
          key: 'minimumCurrentRate',
          label: 'Minimum Current Rate',
          type: 'percentage',
          placeholder: 'Enter minimum %'
        }
      ]
    },
    premiumType: {
      label: 'Premium Type',
      options: ['Single', 'Flexible']
    },
    amBestRating: {
      label: 'AM Best Rating',
      type: 'dropdown',
      options: availableAmBestRatings,
      placeholder: 'Select minimum rating'
    },
    ...(hasFiaProducts() && {
      availableIndexes: {
        label: 'Available Indexes',
        type: 'multiselect',
        options: availableIndexes,
        loading: indexesLoading
      }
    })
  };

  // Check if we have quotes data to enable filters
  const hasQuotesData = quotesData && Array.isArray(quotesData) && quotesData.length > 0;

  // Check if we have original quotes data
  const hasOriginalQuotesData = originalQuotesData && Array.isArray(originalQuotesData) && originalQuotesData.length > 0;

  // Get active filter count
  const getActiveFilterCount = () => {
    if (activeTab === 'new-life') {
      let count = 0;
      Object.entries(alqFilters).forEach(([key, value]) => {
        if (key === 'amBestRating' && value !== '') {
          count += 1; // ADD THIS CONDITION
        } else if (Array.isArray(value)) {
          count += value.length;
        }
      });
      return count;
    } else if (activeTab === 'new-annuity') {
      let count = 0;
      Object.entries(aaqFilters).forEach(([key, value]) => {
        if (key === 'mva' && value === true) {
          count += 1;
        } else if (key === 'amBestRating' && value !== '') {
          count += 1;
        } else if (key === 'riders' && typeof value === 'object') {
          // Count riders filters
          if (value.livingBenefitRider === true) count += 1;
          if (value.ageRate.age !== '' && value.ageRate.rate !== '') count += 1;
        } else if (Array.isArray(value)) {
          count += value.length;
        } else if (key === 'rates' && typeof value === 'object') {
          // Count non-empty rate filters
          if (value.minimumGuaranteePercent !== '') count += 1;
          if (value.minimumCurrentRate !== '') count += 1;
        }
      });
      return count;
    }
    return 0;
  };

  const shouldEnableClearFilters = hasOriginalQuotesData || getActiveFilterCount() > 0 || hasActiveFilters;

  // Handle filter toggle
  const handleFilterToggle = (category, option) => {
    setAlqFilters(prev => {
      const currentSelections = prev[category] || [];
      const isSelected = currentSelections.includes(option);

      return {
        ...prev,
        [category]: isSelected ? currentSelections.filter(item => item !== option) : [...currentSelections, option]
      };
    });
  };

  // Handle AAQ filter toggle
  const handleAaqFilterToggle = (category, option) => {
    if (category === 'mva') {
      // MVA is a boolean toggle
      setAaqFilters(prev => ({
        ...prev,
        mva: !prev.mva
      }));
    } else {
      setAaqFilters(prev => {
        const currentSelections = prev[category] || [];
        const isSelected = currentSelections.includes(option);

        return {
          ...prev,
          [category]: isSelected ? currentSelections.filter(item => item !== option) : [...currentSelections, option]
        };
      });
    }
  };
  // Apply filters
  const handleApplyFilters = () => {
    if (onFiltersApply) {
      // Set flag to prevent filter clearing when applying filters
      isApplyingFilters.current = true;

      if (activeTab === 'new-life') {
        onFiltersApply(alqFilters);
      } else if (activeTab === 'new-annuity') {
        onFiltersApply(aaqFilters);
      }
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    if (activeTab === 'new-life') {
      setAlqFilters({
        healthClass: [],
        flexibleCoverage: [],
        acceleratedDeathBenefit: [],
        additionalRiders: [],
        iulRiders: [],
        easyIssue: [],
        promotions: [],
        eSubmission: [],
        amBestRating: ''
      });
    } else if (activeTab === 'new-annuity') {
      setAaqFilters({
        riders: {
          livingBenefitRider: false,
          ageRate: {
            age: '',
            rate: ''
          }
        },
        surrenderCharge: [],
        guaranteePeriod: [],
        mva: false,
        rates: {
          minimumGuaranteePercent: '',
          minimumCurrentRate: ''
        },
        premiumType: [],
        amBestRating: '',
        availableIndexes: []
      });
    }

    // Call parent's clear filters function to restore original quotes
    if (onFiltersClear) {
      onFiltersClear();
    }
  };

  // Handle AM Best rating change
  const handleAmBestRatingChange = e => {
    const selectedRating = e.target.value;
    setAaqFilters(prev => ({
      ...prev,
      amBestRating: selectedRating
    }));
  };

  // Handle AM Best rating change for Life
  const handleLifeAmBestRatingChange = e => {
    const selectedRating = e.target.value;
    setAlqFilters(prev => ({
      ...prev,
      amBestRating: selectedRating
    }));
  };

  const isAnnuityDisabled = Boolean(universalQuoterDetails?.universal_quoter_details?.find(d => d?.quoter_type === 1)?.is_deactivated);
  const isLifeDisabled = Boolean(universalQuoterDetails?.universal_quoter_details?.find(d => d?.quoter_type === 2)?.is_deactivated);

  const tabs = [
    { id: 'new-annuity', label: 'New Annuity', enabled: !isAnnuityDisabled, tooltip: 'This Quoter is Inactive', isTooltip: true },
    { id: 'new-life', label: 'New Life', enabled: !isLifeDisabled, tooltip: 'This Quoter is Inactive', isTooltip: true },
    { id: 'new-ltc', label: 'New LTC', enabled: false, tooltip: 'Coming Soon', isTooltip: true }
  ];

  const getActionItems = () => {
    let items = [];

    if (activeTab === 'new-life') {
      items = [
        { id: 'quote-setup', label: 'Quote Setup', icon: mdiFileSign },
        { id: 'alq-filters', label: 'Filters', icon: mdiAirFilter },
        { id: 'carriers', label: 'Carriers', icon: mdiDomain },
        { id: 'compare', label: 'Compare', icon: mdiFileCompare },
        { id: 'share', label: 'Share', icon: mdiShareVariant }
      ];
    } else if (activeTab === 'new-ltc') {
      items = [
        { id: 'jurisdiction', label: 'Jurisdiction', icon: mdiMapMarker },
        { id: 'quote-setup', label: 'Quote Setup', icon: mdiFileSign },
        { id: 'ltc-filters', label: 'Filters', icon: mdiAirFilter },
        { id: 'plan', label: 'Plan', icon: mdiClipboardList },
        { id: 'carriers', label: 'Carriers', icon: mdiDomain },
        { id: 'compare', label: 'Compare', icon: mdiFileCompare },
        { id: 'share', label: 'Share', icon: mdiShareVariant }
      ];
    } else {
      items = [
        { id: 'quote-setup', label: 'Quote Setup', icon: mdiFileSign },
        { id: 'aaq-filters', label: 'Filters', icon: mdiAirFilter },
        { id: 'carriers', label: 'Carriers', icon: mdiDomain },
        { id: 'compare', label: 'Compare', icon: mdiFileCompare },
        { id: 'share', label: 'Share', icon: mdiShareVariant }
      ];
    }
    return items.filter(item => {
      if (!hasQuotesData && !hasOriginalQuotesData && currentView !== 'results') {
        return false;
      }
      return true;
    });
  };
  const handleActionItemClick = item => {
    if (item.id === 'quote-setup') {
      // Toggle the quote setup form overlay
      if (onQuoteSetupToggle) {
        onQuoteSetupToggle();
      }
      setSelectedActionItem(selectedActionItem === item.id ? null : item.id);
    } else if (item.id === 'features') {
      setIsFeaturesModalOpen(true);
      setSelectedActionItem(item.id);
    } else if (item.id === 'alq-filters' || item.id === 'aaq-filters' || item.id === 'ltc-filters') {
      setIsAlqFiltersModalOpen(true);
      setSelectedActionItem(item.id);
    } else if (item.id === 'carriers') {
      if (onModalOpen) {
        onModalOpen('carriers');
      }
      setSelectedActionItem(item.id);
    } else if (item.id === 'compare') {
      // Handle compare action
      if (onCompareAction) {
        onCompareAction(compareSelections);
      }
      setSelectedActionItem(item.id);
    } else if (item.id === 'share') {
      // Handle share action
      if (onShareAction) {
        onShareAction(compareSelections);
      }
      setSelectedActionItem(item.id);
    } else {
      // For non-modal items, toggle selection
      setSelectedActionItem(selectedActionItem === item.id ? null : item.id);
    }
  };

  // Get total compare count across all tabs
  const getTotalCompareCount = () => {
    return Object.values(compareSelections).reduce((total, selections) => total + selections.length, 0);
  };

  const closeQuoteSetupModal = () => {
    setIsQuoteSetupModalOpen(false);
    setSelectedActionItem(null);
  };

  const closeFeaturesModal = () => {
    setIsFeaturesModalOpen(false);
    setSelectedActionItem(null);
  };

  const closeAlqFiltersModal = () => {
    setIsAlqFiltersModalOpen(false);
    setSelectedActionItem(null);
  };

  const actionItems = getActionItems();

  const styles = {
    header: {
      display: 'flex',
      padding: '20px 16px 16px 16px',
      flexDirection: 'column',
      alignItems: 'flexStart',
      gap: '16px',
      alignSelf: 'stretch',
      background: '#fff',
      borderBottom: '1px solid #E7E6E6'
    },
    topBar: {
      backgroundColor: '#ffffff',
      padding: '12px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '20px',
      fontWeight: '500',
      margin: '0',
      color: '#333'
    },
    userActions: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center'
    },
    icon: {
      fontSize: '18px',
      color: '#6c757d',
      cursor: 'pointer'
    },
    navigation: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0 24px'
    },
    tabs: {
      display: 'flex',
      padding: '7px 8px',
      alignItems: 'center',
      gap: '2px',
      borderRadius: '80px',
      backgroundColor: '#EEEEF0'
    },
    tab: {
      display: 'flex',
      padding: '8px 16px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '40px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6c757d',
      transition: 'all 0.2s ease'
    },
    activeTab: {
      color: '#333',
      backgroundColor: '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    actions: {
      display: 'flex',
      height: '30px',
      padding: '0px 14px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px'
    },
    actionItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px',
      backgroundColor: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: '#dee2e6',
      borderRadius: '40px',
      cursor: 'pointer',
      fontSize: '13px',
      color: '#6c757d',
      transition: 'all 0.2s ease',
      ':hover': {
        backgroundColor: '#f8f9fa',
        borderColor: '#007bff',
        color: '#007bff'
      }
    },
    actionItemSelected: {
      backgroundColor: '#007bff',
      borderColor: '#007bff',
      color: '#ffffff'
    },
    actionItemWithFilters: {
      backgroundColor: '#28a745',
      borderColor: '#28a745',
      color: '#ffffff',
      position: 'relative'
    },
    filterIndicator: {
      position: 'absolute',
      top: '-5px',
      right: '-5px',
      backgroundColor: '#dc3545',
      color: 'white',
      borderRadius: '12px',
      padding: '2px 6px',
      fontSize: '10px',
      fontWeight: 'bold',
      minWidth: '16px',
      textAlign: 'center'
    },
    compareBadge: {
      position: 'absolute',
      top: '-5px',
      right: '-5px',
      backgroundColor: '#dc3545',
      color: 'white',
      borderRadius: '12px',
      padding: '2px 6px',
      fontSize: '10px',
      fontWeight: 'bold',
      minWidth: '16px',
      textAlign: 'center'
    },
    filtersSection: {
      backgroundColor: '#f8f9fa',
      padding: '16px 24px',
      borderTop: '1px solid #e9ecef'
    },
    filtersHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    },
    filtersTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#333',
      margin: '0'
    },
    filtersActions: {
      display: 'flex',
      gap: '8px'
    },
    filterButton: {
      padding: '6px 12px',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      backgroundColor: '#fff',
      color: '#6c757d',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    applyButton: {
      backgroundColor: '#007bff',
      color: '#fff',
      borderColor: '#007bff'
    },
    filterCategories: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    filterCategory: {
      backgroundColor: '#fff',
      border: '1px solid #e9ecef',
      borderRadius: '6px',
      padding: '12px'
    },
    categoryLabel: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#495057',
      marginBottom: '8px'
    },
    filterOptions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px'
    },
    filterChip: {
      padding: '4px 8px',
      border: '1px solid #dee2e6',
      borderRadius: '16px',
      backgroundColor: '#fff',
      color: '#6c757d',
      fontSize: '11px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      userSelect: 'none'
    },
    filterChipSelected: {
      backgroundColor: '#007bff',
      color: '#fff',
      borderColor: '#007bff'
    },
    filterChipDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      maxWidth: '800px',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      borderBottom: '1px solid #e9ecef',
      paddingBottom: '12px'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#333',
      margin: '0'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#6c757d',
      padding: '0',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabledTab: {
      opacity: 0.5,
      cursor: 'not-allowed',
      position: 'relative'
    },
    tooltip: {
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: '#333',
      color: '#fff',
      padding: '8px 12px',
      borderRadius: '4px',
      fontSize: '12px',
      whiteSpace: 'nowrap',
      zIndex: 10000,
      marginBottom: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      pointerEvents: 'none'
    }
  };

  return (
    <>
      <div ref={headerRef} style={styles.header}>
        <div style={styles.topBar} />

        <div style={styles.navigation}>
          <div style={styles.tabs}>
            {tabs.map(tab => (
              <div
                key={tab.id}
                onMouseEnter={() => {
                  if (!tab.enabled) {
                    setHoveredTab(tab.id);
                  }
                }}
                onMouseLeave={() => {
                  if (!tab.enabled) {
                    setHoveredTab(null);
                  }
                }}
                style={{ position: 'relative' }}
              >
                <button
                  disabled={!isUniversalQuoterLoaded || !tab.enabled}
                  onClick={() => {
                    if (isUniversalQuoterLoaded && tab.enabled) {
                      onTabChange(tab.id);
                    }
                  }}
                  style={{
                    ...styles.tab,
                    ...(activeTab === tab.id ? styles.activeTab : {}),
                    ...(isUniversalQuoterLoaded ? {} : { opacity: 0.5, cursor: 'not-allowed' }),
                    ...(!tab.enabled ? styles.disabledTab : {})
                  }}
                >
                  {tab.label}
                </button>
                {!tab.enabled && hoveredTab === tab.id && tab.isTooltip && (
                  <div style={styles.tooltip}>
                    {tab.tooltip || 'Deactivated'}
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid transparent',
                        borderTop: '5px solid #333'
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={styles.actions}>
            {actionItems.map(item => {
              if (item.id === 'share' && !(getTotalCompareCount() === 1)) return null;
              if (item.id === 'compare' && getTotalCompareCount() === 0) return null;
              return (
                <button
                  key={item.id}
                  onClick={() => handleActionItemClick(item)}
                  onMouseEnter={e => {
                    if (selectedActionItem !== item.id && !(item.id.includes('filters') && getActiveFilterCount() > 0)) {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.borderColor = '#007bff';
                      e.target.style.color = '#007bff';
                    }
                  }}
                  onMouseLeave={e => {
                    if (selectedActionItem !== item.id && !(item.id.includes('filters') && getActiveFilterCount() > 0)) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#dee2e6';
                      e.target.style.color = '#6c757d';
                    }
                  }}
                  style={{
                    ...styles.actionItem,
                    ...(selectedActionItem === item.id ? styles.actionItemSelected : {}),
                    ...(item.id.includes('filters') && getActiveFilterCount() > 0 ? styles.actionItemWithFilters : {}),
                    position: 'relative',
                    ':hover': styles.actionItem[':hover']
                  }}
                >
                  <Icon path={item?.icon || mdiCircle} size={0.7} />
                  <span>{item.label}</span>
                  {item.id === 'compare' && getTotalCompareCount() > 0 && <span style={styles.compareBadge}>{getTotalCompareCount()}</span>}
                  {item.id.includes('filters') && getActiveFilterCount() > 0 && <span style={styles.filterIndicator}>{getActiveFilterCount()}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <QuoteSetupModal isOpen={isQuoteSetupModalOpen} onClose={closeQuoteSetupModal} />
      <FeaturesModal isOpen={isFeaturesModalOpen} onClose={closeFeaturesModal} />

      {/* ALQ Filters Modal */}
      {isAlqFiltersModalOpen && (
        <div onClick={closeAlqFiltersModal} style={styles.modalOverlay}>
          <div onClick={e => e.stopPropagation()} style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{activeTab === 'new-life' ? 'ALQ Filters' : activeTab === 'new-annuity' ? 'AAQ Filters' : 'LTC Filters'}</h2>
              <button onClick={closeAlqFiltersModal} style={styles.closeButton}>
                ×
              </button>
            </div>

            {activeTab === 'new-life' ? (
              <div>
                <div style={styles.filtersActions}>
                  <button
                    disabled={!shouldEnableClearFilters}
                    onClick={handleClearFilters}
                    style={{
                      ...styles.filterButton,
                      ...(shouldEnableClearFilters ? {} : { opacity: 0.5, cursor: 'not-allowed' })
                    }}
                  >
                    Clear All
                  </button>
                  <button
                    disabled={!hasQuotesData}
                    onClick={() => {
                      handleApplyFilters();
                      closeAlqFiltersModal();
                    }}
                    style={{
                      ...styles.filterButton,
                      ...styles.applyButton,
                      ...(hasQuotesData ? {} : { opacity: 0.5, cursor: 'not-allowed' })
                    }}
                  >
                    Apply Filters
                  </button>
                </div>

                {!hasQuotesData && hasOriginalQuotesData && (
                  <div
                    style={{
                      backgroundColor: '#fff3cd',
                      color: '#856404',
                      padding: '12px',
                      borderRadius: '4px',
                      marginBottom: '16px',
                      border: '1px solid #ffeaa7',
                      fontSize: '14px'
                    }}
                  >
                    <strong>No results found with current filters.</strong> You can clear filters above to see all quotes again.
                  </div>
                )}

                <div style={{ ...styles.filterCategories, marginTop: '16px' }}>
                  {Object.entries(alqFilterConfig).map(([categoryKey, category]) => (
                    <div key={categoryKey} style={styles.filterCategory}>
                      <div style={styles.categoryLabel}>{category.label}</div>
                      <div style={styles.filterOptions}>
                        {category.type === 'dropdown' ? (
                          // ADD THIS DROPDOWN RENDERING
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {category.options.length === 0 && hasQuotesData ? (
                              <div
                                style={{
                                  padding: '8px',
                                  fontSize: '12px',
                                  color: '#6c757d',
                                  fontStyle: 'italic',
                                  textAlign: 'center'
                                }}
                              >
                                No AM Best ratings available in current quotes
                              </div>
                            ) : (
                              <select
                                disabled={!hasQuotesData || category.options.length === 0}
                                onChange={handleLifeAmBestRatingChange}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  cursor: hasQuotesData && category.options.length > 0 ? 'pointer' : 'not-allowed',
                                  opacity: hasQuotesData && category.options.length > 0 ? 1 : 0.5,
                                  color: '#495057',
                                  backgroundColor: hasQuotesData && category.options.length > 0 ? '#fff' : '#f8f9fa',
                                  outline: 'none'
                                }}
                                value={alqFilters[categoryKey] || ''}
                              >
                                <option value=''>{category.placeholder || 'Select an option'}</option>
                                {category.options.map(rating => (
                                  <option key={rating} value={rating}>
                                    {rating === 'NA' ? 'NA (No Rating)' : `${rating} or better`}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        ) : (
                          // Regular chip options for other filters
                          category.options.map(option => {
                            const isSelected = alqFilters[categoryKey]?.includes(option);
                            return (
                              <span
                                key={option}
                                onClick={() => hasQuotesData && handleFilterToggle(categoryKey, option)}
                                style={{
                                  ...styles.filterChip,
                                  ...(isSelected ? styles.filterChipSelected : {}),
                                  ...(hasQuotesData ? {} : styles.filterChipDisabled)
                                }}
                              >
                                {option}
                              </span>
                            );
                          })
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : activeTab === 'new-annuity' ? (
              <div>
                <div style={styles.filtersActions}>
                  <button
                    disabled={!shouldEnableClearFilters}
                    onClick={handleClearFilters}
                    style={{
                      ...styles.filterButton,
                      ...(shouldEnableClearFilters ? {} : { opacity: 0.5, cursor: 'not-allowed' })
                    }}
                  >
                    Clear All
                  </button>
                  <button
                    disabled={!hasQuotesData}
                    onClick={() => {
                      handleApplyFilters();
                      closeAlqFiltersModal();
                    }}
                    style={{
                      ...styles.filterButton,
                      ...styles.applyButton,
                      ...(hasQuotesData ? {} : { opacity: 0.5, cursor: 'not-allowed' })
                    }}
                  >
                    Apply Filters
                  </button>
                </div>

                {!hasQuotesData && hasOriginalQuotesData && (
                  <div
                    style={{
                      backgroundColor: '#fff3cd',
                      color: '#856404',
                      padding: '12px',
                      borderRadius: '4px',
                      marginBottom: '16px',
                      border: '1px solid #ffeaa7',
                      fontSize: '14px'
                    }}
                  >
                    <strong>No results found with current filters.</strong> You can clear filters above to see all quotes again.
                  </div>
                )}

                <div style={{ ...styles.filterCategories, marginTop: '16px' }}>
                  {Object.entries(aaqFilterConfig).map(([categoryKey, category]) => (
                    <div key={categoryKey} style={styles.filterCategory}>
                      <div style={styles.categoryLabel}>{category.label}</div>
                      <div style={styles.filterOptions}>
                        {category.type === 'dropdown' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {category.options.length === 0 && hasQuotesData ? (
                              <div
                                style={{
                                  padding: '8px',
                                  fontSize: '12px',
                                  color: '#6c757d',
                                  fontStyle: 'italic',
                                  textAlign: 'center'
                                }}
                              >
                                No AM Best ratings available in current quotes
                              </div>
                            ) : (
                              <select
                                disabled={!hasQuotesData || category.options.length === 0}
                                onChange={handleAmBestRatingChange}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '4px',
                                  fontSize: '13px',
                                  cursor: hasQuotesData && category.options.length > 0 ? 'pointer' : 'not-allowed',
                                  opacity: hasQuotesData && category.options.length > 0 ? 1 : 0.5,
                                  color: '#495057',
                                  backgroundColor: hasQuotesData && category.options.length > 0 ? '#fff' : '#f8f9fa',
                                  outline: 'none'
                                }}
                                value={aaqFilters[categoryKey] || ''}
                              >
                                <option value=''>{category.placeholder || 'Select an option'}</option>
                                {category.options.map(rating => (
                                  <option key={rating} value={rating}>
                                    {rating === 'NA' ? 'NA (No Rating)' : `${rating} or better`}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        ) : category.type === 'riders' ? (
                          // Special handling for riders filters
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {/* Living Benefit Rider Checkbox */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                checked={aaqFilters.riders.livingBenefitRider}
                                disabled={!hasQuotesData}
                                onChange={e => {
                                  if (hasQuotesData) {
                                    setAaqFilters(prev => ({
                                      ...prev,
                                      riders: {
                                        ...prev.riders,
                                        livingBenefitRider: e.target.checked
                                      }
                                    }));
                                  }
                                }}
                                style={{ cursor: hasQuotesData ? 'pointer' : 'not-allowed' }}
                                type='checkbox'
                              />
                              <label
                                style={{
                                  fontSize: '11px', // Changed from 12px to match filterChip
                                  cursor: hasQuotesData ? 'pointer' : 'not-allowed',
                                  color: hasQuotesData ? '#6c757d' : '#6c757d', // Match filterChip color
                                  userSelect: 'none' // Match filterChip behavior
                                }}
                              >
                                Living Benefit Rider (GMWB)
                              </label>
                            </div>

                            {/* Age-Based Rate Filter */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <label
                                style={{
                                  fontSize: '11px', // Changed from 12px to match filterChip
                                  fontWeight: '600',
                                  color: '#495057'
                                }}
                              >
                                Age-Based Rate Filter:
                              </label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <select
                                  disabled={!hasQuotesData}
                                  onChange={e => {
                                    if (hasQuotesData) {
                                      setAaqFilters(prev => ({
                                        ...prev,
                                        riders: {
                                          ...prev.riders,
                                          ageRate: {
                                            ...prev.riders.ageRate,
                                            age: e.target.value
                                          }
                                        }
                                      }));
                                    }
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    fontSize: '11px', // Changed from 12px to match filterChip
                                    cursor: hasQuotesData ? 'pointer' : 'not-allowed',
                                    opacity: hasQuotesData ? 1 : 0.5,
                                    color: '#6c757d', // Match filterChip color
                                    backgroundColor: hasQuotesData ? '#fff' : '#f8f9fa' // Visual feedback when disabled
                                  }}
                                  value={aaqFilters?.riders?.ageRate?.age}
                                >
                                  <option value=''>Select Age</option>
                                  {[50, 55, 60, 65, 70].map(age => (
                                    <option key={age} value={age}>
                                      {age}
                                    </option>
                                  ))}
                                </select>

                                <input
                                  disabled={!hasQuotesData}
                                  max='100'
                                  min='0'
                                  onChange={e => {
                                    if (hasQuotesData) {
                                      setAaqFilters(prev => ({
                                        ...prev,
                                        riders: {
                                          ...prev.riders,
                                          ageRate: {
                                            ...prev.riders.ageRate,
                                            rate: e.target.value
                                          }
                                        }
                                      }));
                                    }
                                  }}
                                  placeholder='Min Rate %'
                                  step='0.01'
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    fontSize: '11px', // Changed from 12px to match filterChip
                                    width: '80px',
                                    cursor: hasQuotesData ? 'text' : 'not-allowed',
                                    opacity: hasQuotesData ? 1 : 0.5,
                                    color: '#6c757d', // Match filterChip color
                                    backgroundColor: hasQuotesData ? '#fff' : '#f8f9fa' // Visual feedback when disabled
                                  }}
                                  type='number'
                                  value={aaqFilters?.riders?.ageRate?.rate}
                                />
                                <span
                                  style={{
                                    fontSize: '11px', // Changed from 12px to match filterChip
                                    color: '#6c757d'
                                  }}
                                >
                                  %
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : category.type === 'checkbox' ? (
                          // Special handling for MVA checkbox
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: hasQuotesData ? 'pointer' : 'not-allowed' }}>
                            <input
                              checked={aaqFilters.mva}
                              disabled={!hasQuotesData}
                              onChange={() => hasQuotesData && handleAaqFilterToggle(categoryKey, category.options[0])}
                              style={{ cursor: hasQuotesData ? 'pointer' : 'not-allowed' }}
                              type='checkbox'
                            />
                            {category.options[0]}
                          </label>
                        ) : category.type === 'rates' ? (
                          // Special handling for rates filters
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {category.options.map(rateOption => (
                              <div key={rateOption.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{ fontSize: '12px', minWidth: '140px', color: '#495057' }}>{rateOption.label}:</label>
                                <input
                                  disabled={!hasQuotesData}
                                  max='100'
                                  min='0'
                                  onChange={e => {
                                    if (hasQuotesData) {
                                      setAaqFilters(prev => ({
                                        ...prev,
                                        rates: {
                                          ...prev.rates,
                                          [rateOption.key]: e.target.value
                                        }
                                      }));
                                    }
                                  }}
                                  placeholder={rateOption.placeholder}
                                  step='0.01'
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    width: '80px',
                                    cursor: hasQuotesData ? 'text' : 'not-allowed',
                                    opacity: hasQuotesData ? 1 : 0.5
                                  }}
                                  type='number'
                                  value={aaqFilters.rates?.[rateOption.key] || ''}
                                />
                                <span style={{ fontSize: '12px', color: '#6c757d' }}>%</span>
                              </div>
                            ))}
                          </div>
                        ) : category.type === 'multiselect' ? (
                          // Special handling for multiselect (Available Indexes)
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {category.loading ? (
                              <div style={{ padding: '8px', fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>Loading indexes...</div>
                            ) : category.options.length > 0 ? (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                  maxHeight: '150px',
                                  overflowY: 'auto',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '4px',
                                  padding: '8px'
                                }}
                              >
                                {category.options.map(option => {
                                  const isSelected = aaqFilters[categoryKey]?.includes(option.value);
                                  return (
                                    <label
                                      key={option.value}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '12px',
                                        cursor: hasQuotesData ? 'pointer' : 'not-allowed',
                                        padding: '2px 0'
                                      }}
                                    >
                                      <input
                                        checked={isSelected}
                                        disabled={!hasQuotesData}
                                        onChange={() => hasQuotesData && handleAaqFilterToggle(categoryKey, option.value)}
                                        style={{ cursor: hasQuotesData ? 'pointer' : 'not-allowed' }}
                                        type='checkbox'
                                      />
                                      {option.label}
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ padding: '8px', fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>No indexes available</div>
                            )}
                          </div>
                        ) : (
                          // Regular chip options
                          category.options.map(option => {
                            const isSelected = aaqFilters[categoryKey]?.includes(option);
                            return (
                              <span
                                key={option}
                                onClick={() => hasQuotesData && handleAaqFilterToggle(categoryKey, option)}
                                style={{
                                  ...styles.filterChip,
                                  ...(isSelected ? styles.filterChipSelected : {}),
                                  ...(hasQuotesData ? {} : styles.filterChipDisabled)
                                }}
                              >
                                {option}
                              </span>
                            );
                          })
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                <p>Filters for LTC quotes are coming soon!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

QuoterHeader.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  isUniversalQuoterLoaded: PropTypes.bool,
  compareSelections: PropTypes.object,
  onCompareAction: PropTypes.func,
  quotesData: PropTypes.array,
  originalQuotesData: PropTypes.array,
  onFiltersApply: PropTypes.func,
  onFiltersClear: PropTypes.func,
  hasActiveFilters: PropTypes.bool,
  onModalOpen: PropTypes.func,
  onQuoteSetupToggle: PropTypes.func,
  currentView: PropTypes.string, //'form', 'loading', or 'results'
  formData: PropTypes.object,
  onShareAction: PropTypes.func,
  universalQuoterDetails: PropTypes.object
};

export default QuoterHeader;
